/**
 * @module components/OutputBar
 * @description Icon toolbar that exposes the output engine for one rendered
 * card: print, download PNG, native share, email, social links, copy link.
 * Icons replace text labels (Samantha, 2026-10-01); every icon keeps an
 * accessible name and a tooltip, and the line beneath invites sharing.
 *
 * Every action reports what happened in the status line, so "nothing
 * happened" is never the result (Samantha reported sharing "not working").
 */
import { useEffect, useRef, useState, type RefObject } from "react";
import {
    SHARE_IMAGE_WIDTH_PX,
    downloadBlob,
    nativeShare,
    renderElementToPng,
    shareLinks,
    type NativeShareResult,
    type SharePayload,
} from "../lib/outputEngine";
import { Icon, type IconName } from "./Icons";

export interface OutputBarProps {
    /** The card face exported as PNG and attached to native shares. */
    targetRef: RefObject<HTMLElement | null>;
    payload: SharePayload;
    filename: string;
    /** Prints this card (front and back) through the shared print service. */
    onPrint: () => void;
}

/** One icon control. Links and buttons share the look and the labeling. */
function IconAction({ icon, label, href, onClick }: { icon: IconName; label: string; href?: string; onClick?: () => void }) {
    const common = { className: "icon-btn", "aria-label": label, title: label };
    return href ? (
        <a {...common} href={href} target={href.startsWith("mailto:") ? undefined : "_blank"} rel="noopener noreferrer" onClick={onClick}>
            <Icon name={icon} />
        </a>
    ) : (
        <button {...common} type="button" onClick={onClick}>
            <Icon name={icon} />
        </button>
    );
}

/** Plain-language result of the share sheet. */
const SHARE_MESSAGES: Record<NativeShareResult, string> = {
    shared: "Shared. Thank you for spreading the love.",
    cancelled: "",
    unsupported: "This browser has no share sheet, so the link was copied. Paste it into any message or post.",
    blocked: "The browser blocked the share sheet. Tap Share again.",
    failed: "The share sheet didn't open on this device, so the link was copied instead.",
};

export function OutputBar({ targetRef, payload, filename, onPrint }: OutputBarProps) {
    const [status, setStatus] = useState<string>("");
    const shareImage = useRef<Blob | null>(null);
    const links = shareLinks(payload);

    // Render the share image ahead of time, so a tap on Share can open the
    // share sheet immediately (see nativeShare's BUG FIX note).
    useEffect(() => {
        shareImage.current = null;
        let cancelled = false;
        const timer = window.setTimeout(() => {
            const el = targetRef.current;
            if (!el) return;
            renderElementToPng(el, SHARE_IMAGE_WIDTH_PX)
                .then((blob) => {
                    if (!cancelled) shareImage.current = blob;
                })
                .catch((error: unknown) => console.error("[OutputBar] Share image pre-render failed; links still share", error));
        }, 400);
        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [payload.url, targetRef]);

    const say = (message: string, clearAfterMs = 0) => {
        setStatus(message);
        if (clearAfterMs) window.setTimeout(() => setStatus(""), clearAfterMs);
    };

    const copyLink = async (): Promise<boolean> => {
        try {
            await navigator.clipboard.writeText(payload.url);
            return true;
        } catch (error) {
            console.error("[OutputBar] Copy failed", error);
            say(`Couldn't copy automatically. The link is: ${payload.url}`);
            return false;
        }
    };

    /** Must stay free of awaits before nativeShare(): the share sheet needs the tap to still be "live". */
    const share = () => {
        nativeShare(payload, shareImage.current).then(async (result) => {
            if (result === "unsupported" || result === "failed") {
                if (await copyLink()) say(SHARE_MESSAGES[result]);
                return;
            }
            say(SHARE_MESSAGES[result], 4000);
        });
    };

    const download = async () => {
        const el = targetRef.current;
        if (!el) return;
        try {
            say("Rendering image…");
            downloadBlob(await renderElementToPng(el), filename);
            say("Image downloaded.", 3000);
        } catch (error) {
            console.error("[OutputBar] Image export failed", error);
            say(`Image export failed: ${error instanceof Error ? error.message : String(error)}`);
        }
    };

    return (
        <div className="output-bar">
            <div className="output-icons" role="toolbar" aria-label="Print and share this story">
                <IconAction icon="print" label="Print this postcard" onClick={onPrint} />
                <IconAction icon="download" label="Download image" onClick={download} />
                <IconAction icon="share" label="Share… (your device's share sheet)" onClick={share} />
                <IconAction icon="email" label="Share by email (opens your email app)" href={links.email} onClick={() => say("Opening your email app… If nothing opens, this device has no email app set up; use Copy link instead.", 8000)} />
                <IconAction icon="facebook" label="Share on Facebook" href={links.facebook} />
                <IconAction icon="x" label="Share on X" href={links.x} />
                <IconAction icon="linkedin" label="Share on LinkedIn" href={links.linkedin} />
                <IconAction icon="whatsapp" label="Share on WhatsApp" href={links.whatsapp} />
                <IconAction icon="link" label="Copy link" onClick={() => copyLink().then((ok) => ok && say("Link copied. Paste it into any message or post.", 3000))} />
            </div>
            <p className="output-invite">Share this story. Spread the love for Jamaica.</p>
            <span className="output-status" aria-live="polite">{status}</span>
        </div>
    );
}
